import {useInfiniteQuery} from "@tanstack/react-query";
import {ApiConfig} from "@/config/api.config";
import {ApiHookProps} from '@/@types/api.types';
import {GetApartmentListItemResponseDtoListResponseDto} from '@/features/apartments/types/apartments-dto';
import {UrlHelper} from '@/utils/UrlHelper';
import {useAxiosAuth} from "@/hooks/useAxiosAuth.ts";
import {useHotelStorage} from '@/features/hotels/hooks/useHotelStorage.ts';
import {useCurrencyStore} from "@/store/useCurrencyStore.ts";

const PAGE_SIZE = 20;

type Props = {
    take?: number;
    skip?: number;
    ownerId?: string;
} & ApiHookProps;

export const useApartmentsListApi = ({searchText, take = PAGE_SIZE, skip, ownerId}: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const {currentCurrency} = useCurrencyStore();
    const selectedHotelId = useHotelStorage((s) => s.selectedHotel?.id);

    const {
        data: apartmentsListData,
        isLoading: isLoadingApartmentsListData,
        isError: isErrorApartmentsListData,
        fetchNextPage: fetchApartmentsListDataNextPage,
        hasNextPage: hasApartmentsListDataNextPage,
        isFetchingNextPage: isFetchingApartmentsListDataNextPage
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh,
            ApiConfig.queries.features.apartments.queryKeys.list,
            selectedHotelId,
            searchText, take, skip, ownerId, currentCurrency
        ],
        queryFn: async ({pageParam}): Promise<GetApartmentListItemResponseDtoListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.apartments.endpoints.list, {
                searchText, take, skip: pageParam, ownerId
            });
            const response = await axiosInstance.get<GetApartmentListItemResponseDtoListResponseDto>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedData = apartmentsListData?.pages.flatMap((page) => page.items ?? []);
    const apartmentsTotalCount = apartmentsListData?.pages[0].totalCount;
    const ownersTotalCount = apartmentsListData?.pages[0].ownersTotalCount;

    return {
        apartmentsListData: fetchedData,
        apartmentsTotalCount,
        ownersTotalCount,
        isLoadingApartmentsListData,
        isErrorApartmentsListData,
        fetchApartmentsListDataNextPage,
        hasApartmentsListDataNextPage,
        isFetchingApartmentsListDataNextPage,
    }
}